import * as React from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Box, Card, Grid, CardContent, CardMedia, Typography, Button, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// ✅ Foto genérica: se usa para TODAS las actividades mientras la API no esté
// devolviendo bien las fotos propias de cada actividad (HotProdFoto).
import genericActivityImage from './imagenesPruebas/cabalgata.jpg';

import DialogMoreInfoActivity from '../components/activities/DialogMoreInfoActivity';
import { obtenerActividades } from '../services/motorService';

// ✅ El itinerario (HotProdItinerario) viene como HTML (<p>, <div>, etc).
// Lo limpiamos para poder mostrarlo como texto plano, conservando saltos de
// línea donde había párrafos/<br> para que se lea ordenado.
const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();
};

const truncate = (text, maxLength = 160) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}…` : text;
};

function Activities() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hotCod = params.get('hotcod') ?? Cookies.get('hotCod') ?? null;

  const [activitiesList, setActivitiesList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [errorMsg, setErrorMsg] = React.useState(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedActivity, setSelectedActivity] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;

    const fetchActivities = async () => {
      if (!hotCod) {
        setLoading(false);
        setErrorMsg('No se pudo determinar el hotel para buscar actividades.');
        return;
      }

      try {
        setLoading(true);
        setErrorMsg(null);

        const data = await obtenerActividades(hotCod);
        const coleccion = data?.ColeccionActividades || [];

        const mapped = coleccion.map((item) => ({
          id: item.HotProdCod,
          activityName: item.HotProdNomLargo,
          description: stripHtml(item.HotProdItinerario),
          // ✅ Foto genérica por ahora (ver comentario del import más arriba).
          image: genericActivityImage,
          conditions: '',
          price: null,
          currency: '',
        }));

        if (isMounted) setActivitiesList(mapped);
      } catch (err) {
        console.error('Error al cargar actividades:', err);
        if (isMounted) setErrorMsg('No se pudieron cargar las actividades disponibles.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchActivities();
    return () => {
      isMounted = false;
    };
  }, [hotCod]);

  const handleClickOpen = (activity) => {
    setSelectedActivity(activity);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Typography variant="body1" color="error" sx={{ py: 2 }}>
        {errorMsg}
      </Typography>
    );
  }

  if (activitiesList.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
        No hay actividades disponibles para este hotel.
      </Typography>
    );
  }

  return (
    <div>
      <Grid container spacing={3}>
        {
          activitiesList.map((activity) => (
            <Grid item xs={12} md={6} key={activity.id}>
              <Card>
                <CardMedia
                  component="img"
                  height="200"
                  image={activity.image}
                  alt={activity.activityName}
                />
                <CardContent>
                  <Typography variant="h5" component="div">
                    {activity.activityName}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    component="div"
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    {truncate(activity.description)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    {/* ✅ Un único botón por actividad: abre el mismo diálogo que antes
                        mostraba "Más información". Ahí es donde ahora se elige día/hora
                        y se ve el precio, y desde ahí se agrega la actividad. */}
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={() => handleClickOpen(activity)}
                    >
                      Consultar actividad
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        }
      </Grid>
      <DialogMoreInfoActivity open={dialogOpen} handleClose={handleClose} activity={selectedActivity} />
    </div>
  );
}

export default Activities;